import { useState, useCallback } from 'react';
import { Upload, FileText, Loader2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function FileDropZone({ apiKey, onFileReady, accept, label }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState(null);
  const [error, setError] = useState(null);

  const upload = useCallback(async (file) => {
    if (!apiKey) { setError('Enter your API key first'); return; }
    setUploading(true); setError(null); setProgress(10);

    try {
      // Auto-create bucket (ignore error if it already exists)
      try {
        await axios.post(`${API_URL}/api/v1/storage/buckets`, { bucket_name: 'playground-uploads' }, {
          headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' }
        });
      } catch {}
      setProgress(20);

      const objectName = `${Date.now()}-${file.name}`;
      const form = new FormData();
      form.append('bucket_name', 'playground-uploads');
      form.append('object_name', objectName);
      form.append('file', file);

      await axios.post(`${API_URL}/api/v1/storage/upload`, form, {
        headers: { 'X-API-Key': apiKey },
        onUploadProgress: (e) => setProgress(20 + Math.round((e.loaded / e.total) * 50))
      });
      setProgress(80);

      const presigned = await axios.post(`${API_URL}/api/v1/storage/presigned`, {
        bucket_name: 'playground-uploads',
        object_name: objectName,
        method: 'GET',
        expires_seconds: 3600
      }, { headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' } });

      setProgress(100);
      const url = presigned.data.url;
      setUploaded({ name: file.name, size: file.size, url });
      onFileReady(url, file.name);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'object' ? (detail.message || JSON.stringify(detail)) : (detail || 'Upload failed. Make sure MinIO (storage) is in your subscription.'));
    }
    setUploading(false);
  }, [apiKey, onFileReady]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  }, [upload]);

  const onSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  }, [upload]);

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">{label || 'Upload a file'}</p>
      <div
        data-testid="file-drop-zone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
          dragging ? 'border-primary bg-primary/5' : uploaded ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border/50 hover:border-primary/30'
        }`}
        onClick={() => !uploading && document.getElementById('pg-file-input')?.click()}
      >
        <input id="pg-file-input" type="file" accept={accept} className="hidden" onChange={onSelect} />

        {uploading ? (
          <div className="space-y-2">
            <Loader2 className="w-6 h-6 mx-auto text-primary animate-spin" />
            <p className="text-xs text-muted-foreground">Uploading...</p>
            <Progress value={progress} className="h-1.5" />
          </div>
        ) : uploaded ? (
          <div className="space-y-1">
            <Check className="w-6 h-6 mx-auto text-emerald-400" />
            <p className="text-xs font-medium text-emerald-400">{uploaded.name}</p>
            <p className="text-[10px] text-muted-foreground">{(uploaded.size / 1024).toFixed(1)} KB - Ready to process</p>
            <Button size="sm" variant="ghost" className="text-[10px] h-5 mt-1" onClick={(e) => { e.stopPropagation(); setUploaded(null); setProgress(0); }}>
              <X className="w-3 h-3 mr-1" />Change file
            </Button>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
            <p className="text-xs text-muted-foreground">Drag & drop a file here, or click to browse</p>
            {accept && <p className="text-[10px] text-muted-foreground/60">Accepts: {accept}</p>}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
